import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { devError } from '../lib/logger';
import api, { getImageUrl } from '../lib/api';
import { downloadPdfWithCache } from '../lib/pdf';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CatalogPreviewModal from '../components/CatalogPreviewModal';
import CatalogHistoryPanel from '../components/CatalogHistoryPanel';
import { ArrowLeft, Plus, GripVertical, X, Search, Save, FolderPlus, Eye, BookmarkPlus, BookmarkCheck, Palette, Sparkles, BookOpen, Loader2, History, Upload, Filter, RotateCcw, Hash, Copy, Pencil, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'hi', label: 'Hindi' }, { code: 'ta', label: 'Tamil' },
  { code: 'gu', label: 'Gujarati' }, { code: 'te', label: 'Telugu' }, { code: 'pa', label: 'Punjabi' },
  { code: 'ml', label: 'Malayalam' }, { code: 'mr', label: 'Marathi' }, { code: 'bn', label: 'Bengali' },
  { code: 'or', label: 'Odia' }, { code: 'as', label: 'Assamese' }, { code: 'kn', label: 'Kannada' }
];

const CURRENCIES = [
  { code: 'INR', symbol: '\u20B9',         label: 'INR \u20B9' },
  { code: 'USD', symbol: '$',              label: 'USD $' },
  { code: 'GBP', symbol: '\u00A3',         label: 'GBP \u00A3' },
  { code: 'EUR', symbol: '\u20AC',         label: 'EUR \u20AC' },
  { code: 'CAD', symbol: '$',              label: 'CAD $' },
  { code: 'AUD', symbol: 'A$',             label: 'AUD A$' },
  { code: 'AED', symbol: '\u062F.\u0625',  label: 'AED \u062F.\u0625' },
];

// Hoisted to module scope so React doesn't see a fresh component type on
// every render (prevents the entire subtree from unmounting + re-mounting).
function FieldLabel({ children }) {
  return <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">{children}</Label>;
}

function SortableProduct({ product, onRemove, onUpdateDisplay }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  // iter30 — grouped SKU detection. Only grouped SKUs get a display-mode
  // toggle; standalone products don't need it. _group_siblings is populated
  // during catalog hydration (edit mode) OR we fall back to design_group_id
  // for products just added from the browser (which may not have siblings
  // hydrated yet — the server will still honor the chosen mode on save).
  const siblings = (Array.isArray(product._group_siblings) && product._group_siblings.length)
    ? product._group_siblings
    : (product._editor_siblings || []);
  const hasGroup = siblings.length > 1;
  const canToggle = hasGroup || !!product.design_group_id;
  const isSolo = product._display_mode === 'solo';
  const selectedSku = product._selected_sku || product.product_code;
  return (
    <div ref={setNodeRef} style={style} className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-sm mb-2 group" data-testid={`sortable-product-${product.id}`}>
      <div className="flex items-center gap-3 p-3">
        <div {...attributes} {...listeners} className="drag-handle"><GripVertical size={16} className="text-[#E5E0D8]" /></div>
        {product.cover_image ? <img src={getImageUrl(product.cover_image)} alt="" className="w-10 h-10 object-cover rounded-sm" /> : <div className="w-10 h-10 img-placeholder rounded-sm text-[8px]">N/A</div>}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1A1A1A] truncate">{product.product_name}</p>
          <p className="text-[10px] text-[#C5A059] tracking-wider">{product.product_code}{canToggle ? ` · ${isSolo ? 'solo' : 'full table'}` : ''}</p>
        </div>
        <span className="text-xs text-[#5C5C5C]">{product._display_price || (product.mrp ? `\u20B9${product.mrp}` : '')}</span>
        <button onClick={() => onRemove(product.id)} className="opacity-0 group-hover:opacity-100 text-[#9E2A2B] transition-opacity"><X size={14} /></button>
      </div>
      {canToggle && (
        <div className="px-3 pb-3 border-t border-dashed border-[#E5E0D8] flex items-center gap-2 pt-2" data-testid={`display-mode-row-${product.id}`}>
          <Select value={product._display_mode || 'table'} onValueChange={v => onUpdateDisplay(product.id, { _display_mode: v })}>
            <SelectTrigger className="h-7 text-[11px] rounded-sm border-[#E5E0D8] bg-white w-44" data-testid={`display-mode-select-${product.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="table">Full size table (default)</SelectItem>
              <SelectItem value="solo">Only selected size</SelectItem>
            </SelectContent>
          </Select>
          {isSolo && (
            siblings.length > 0 ? (
              <Select value={selectedSku} onValueChange={v => onUpdateDisplay(product.id, { _selected_sku: v })}>
                <SelectTrigger className="h-7 text-[11px] rounded-sm border-[#E5E0D8] bg-white flex-1" data-testid={`sku-picker-${product.id}`}>
                  <SelectValue placeholder="Pick the size to display" />
                </SelectTrigger>
                <SelectContent>
                  {siblings.map(sib => (
                    <SelectItem key={sib.product_code} value={sib.product_code}>
                      {sib.product_code} — {sib.size || 'size?'} — {'\u20B9'}{sib.mrp ?? '?'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-[10px] text-[#C5A059] italic flex-1">Save the catalog once — the SKU picker will populate after sibling SKUs load.</span>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Sortable wrapper for an entire section. Drag handle (left, dual-grip icon) is
// the ONLY way to reorder sections so it never conflicts with product drag.
// Section ids in DnD are prefixed `sec::` to disambiguate from product ids.
function SortableSection({ section, isActive, onActivate, onRemove, onRenameStart, children }) {
  const sortId = `sec::${section.id}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-sm p-4 ${isActive ? 'border-[#C5A059]' : 'border-[#E5E0D8]'}`}
      onClick={onActivate}
      data-testid={`section-${section.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            {...attributes}
            {...listeners}
            type="button"
            onClick={e => e.stopPropagation()}
            className="cursor-grab active:cursor-grabbing text-[#C5A059] hover:bg-[#F2E8D5] rounded p-1 -ml-1"
            title="Drag to reorder section"
            aria-label="Drag section"
            data-testid={`section-drag-${section.id}`}
          >
            <GripVertical size={16} />
          </button>
          {children.header}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] text-[#5C5C5C]">{section.products.length} products</span>
          <button
            onClick={e => { e.stopPropagation(); onRenameStart(); }}
            className="text-[#5C5C5C] hover:text-[#C5A059] hover:bg-[#F2E8D5] p-1 rounded transition-colors"
            title="Rename section"
            data-testid={`section-rename-${section.id}`}
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); onRemove(); }}
            className="text-[#9E2A2B] hover:bg-red-50 p-1 rounded"
            title="Remove section"
            data-testid={`section-remove-${section.id}`}
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {children.body}
    </div>
  );
}

export default function CatalogBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ title: '', subtitle: '', cover_image: '', intro_note: '', footer_contact: '', status: 'draft' });
  const [branding, setBranding] = useState({ logo_url: '', primary_color: '#C5A059', company_name: 'AARYA24kt', tagline: '' });
  const [catalogLang, setCatalogLang] = useState('en');
  const [showPrices, setShowPrices] = useState(true);
  const [showQuantity, setShowQuantity] = useState(false);
  // Missing-quantities panel state. Opens inside Catalog Details when
  // showQuantity is ON. Keyed by product_code -> local draft value.
  const [missingList, setMissingList] = useState([]);  // [{product_code, product_name, size, current_quantity}]
  const [presentList, setPresentList] = useState([]);
  const [missingDrafts, setMissingDrafts] = useState({});  // product_code -> string
  const [savingQty, setSavingQty] = useState(false);
  const [qtyPanelLoading, setQtyPanelLoading] = useState(false);
  const [currency, setCurrency] = useState({ code: 'INR', symbol: '\u20B9', exchange_rate_to_inr: '', rounding_mode: 'nearest_1' });
  const [backCover, setBackCover] = useState({ name: '', phone: '', address: '', website: '', email: '' });
  const [sections, setSections] = useState([]);
  const [browseProducts, setBrowseProducts] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ brand: [], category: [], subcategory: [], deity: [], region: [], occasion: [] });
  const [newSectionName, setNewSectionName] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadTemplateOpen, setLoadTemplateOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiReasoning, setAiReasoning] = useState('');
  const [catalogShareToken, setCatalogShareToken] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [showAdvFilters, setShowAdvFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', brand: '', category: '', subcategory: '', deity: '', region: '', occasion: '', status: '', mrp_min: '', mrp_max: '' });
  const [codesInput, setCodesInput] = useState('');
  const [codesLoading, setCodesLoading] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateForm, setDuplicateForm] = useState({ title: '', subtitle: '', client_name: '' });
  const [duplicating, setDuplicating] = useState(false);

  const fetchTaxonomy = useCallback(async () => {
    try {
      const types = ['brand', 'category', 'subcategory', 'deity', 'region', 'occasion'];
      const results = await Promise.all(types.map(t => api.get(`/taxonomy?type=${t}`)));
      const tax = {};
      types.forEach((t, i) => { tax[t] = results[i].data; });
      setTaxonomy(tax);
    } catch (err) { devError('Taxonomy load failed', err); }
  }, []);
  const fetchTemplates = useCallback(async () => { try { const { data } = await api.get('/catalog-templates'); setTemplates(data); } catch (err) { devError('Templates load failed', err); } }, []);
  const fetchCatalog = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/catalogs/${id}`);
      setMeta({ title: data.title, subtitle: data.subtitle || '', cover_image: data.cover_image || '', intro_note: data.intro_note || '', footer_contact: data.footer_contact || '', status: data.status || 'draft' });
      if (data.branding) setBranding({ logo_url: data.branding.logo_url || '', primary_color: data.branding.primary_color || '#C5A059', company_name: data.branding.company_name || 'AARYA24kt', tagline: data.branding.tagline || '' });
      if (data.language) setCatalogLang(data.language);
      if (typeof data.show_prices === 'boolean') setShowPrices(data.show_prices);
      if (typeof data.show_quantity === 'boolean') setShowQuantity(data.show_quantity);
      if (data.currency) setCurrency({
        code: data.currency.code || 'INR',
        symbol: data.currency.symbol || '\u20B9',
        exchange_rate_to_inr: data.currency.exchange_rate_to_inr ?? '',
        rounding_mode: data.currency.rounding_mode || 'nearest_1',
      });
      if (data.back_cover) setBackCover(prev => ({ ...prev, ...data.back_cover }));
      setSections(data.sections || []);
      if (data.sections?.length > 0) setActiveSection(data.sections[0].id);
      if (data.share_token) setCatalogShareToken(data.share_token);
    } catch { toast.error('Failed to load catalog'); navigate('/catalogs'); }
  }, [id, navigate]);

  const searchProducts = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (filters.search) params.search = filters.search;
      if (filters.brand) params.brand = filters.brand;
      if (filters.category) params.category = filters.category;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      if (filters.deity) params.deity = filters.deity;
      if (filters.region) params.region = filters.region;
      if (filters.occasion) params.occasion = filters.occasion;
      if (filters.status) params.status = filters.status;
      if (filters.mrp_min) params.mrp_min = parseFloat(filters.mrp_min);
      if (filters.mrp_max) params.mrp_max = parseFloat(filters.mrp_max);
      const { data } = await api.get('/products', { params });
      setBrowseProducts(data.products);
    } catch (err) { devError('Product search failed', err); }
  }, [filters]);

  useEffect(() => { fetchTaxonomy(); fetchCatalog(); fetchTemplates(); }, [fetchTaxonomy, fetchCatalog, fetchTemplates]);
  useEffect(() => { searchProducts(); }, [searchProducts]);

  const addSection = () => { if (!newSectionName.trim()) return; const sec = { id: `section-${Date.now()}`, name: newSectionName.trim(), products: [] }; setSections(prev => [...prev, sec]); setActiveSection(sec.id); setNewSectionName(''); };
  const removeSection = (secId) => { setSections(prev => prev.filter(s => s.id !== secId)); if (activeSection === secId) setActiveSection(sections[0]?.id || null); };
  // Inline rename: which section is currently being renamed, and the staged new name.
  const [renamingSectionId, setRenamingSectionId] = useState(null);
  const [renamingValue, setRenamingValue] = useState('');
  const startRenameSection = (sec) => { setRenamingSectionId(sec.id); setRenamingValue(sec.name); };
  const cancelRenameSection = () => { setRenamingSectionId(null); setRenamingValue(''); };
  const commitRenameSection = () => {
    const trimmed = (renamingValue || '').trim();
    if (!trimmed) { toast.error('Section name cannot be empty'); return; }
    setSections(prev => prev.map(s => s.id === renamingSectionId ? { ...s, name: trimmed } : s));
    setRenamingSectionId(null); setRenamingValue('');
  };
  const addProductToSection = (product) => {
    if (!activeSection) { toast.error('Select or create a section first'); return; }
    // iter30 — seed display_mode default. Grouped SKUs stay in table mode
    // unless the user explicitly flips them. Standalone products ignore the field.
    const seeded = { ...product, _display_mode: product._display_mode || 'table', _selected_sku: product._selected_sku || product.product_code };
    setSections(prev => prev.map(s => { if (s.id !== activeSection) return s; if (s.products.some(p => p.id === product.id)) { toast.info('Already in section'); return s; } return { ...s, products: [...s.products, seeded] }; }));
  };
  const removeProductFromSection = (secId, productId) => { setSections(prev => prev.map(s => s.id === secId ? { ...s, products: s.products.filter(p => p.id !== productId) } : s)); };
  // iter30 — flip a selected product between table and solo. Preserves other
  // state untouched; just updates the inline display mode + selected SKU.
  const updateProductDisplay = (secId, productId, patch) => {
    setSections(prev => prev.map(s => s.id === secId ? { ...s, products: s.products.map(p => p.id === productId ? { ...p, ...patch } : p) } : s));
  };
  // Unified drag handler: section ids are prefixed `sec::` to disambiguate.
  // Section drag => reorder sections; product drag => reorder within its section.
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeIsSection = String(active.id).startsWith('sec::');
    const overIsSection = String(over.id).startsWith('sec::');
    if (activeIsSection && overIsSection) {
      setSections(prev => {
        const oi = prev.findIndex(s => `sec::${s.id}` === active.id);
        const ni = prev.findIndex(s => `sec::${s.id}` === over.id);
        if (oi === -1 || ni === -1) return prev;
        return arrayMove(prev, oi, ni);
      });
      return;
    }
    // Product drag — reorder within the section that owns the active product.
    setSections(prev => prev.map(s => {
      const oi = s.products.findIndex(p => p.id === active.id);
      const ni = s.products.findIndex(p => p.id === over.id);
      if (oi !== -1 && ni !== -1) return { ...s, products: arrayMove(s.products, oi, ni) };
      return s;
    }));
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCoverUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await api.post('/upload', fd); setMeta(p => ({ ...p, cover_image: data.url })); toast.success('Cover image uploaded'); }
    catch { toast.error('Upload failed'); }
    setCoverUploading(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    // Client-side validation — hard limit at 5 MB to match backend/object-storage upload budget.
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5 MB'); return; }
    const okTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!okTypes.includes(file.type)) { toast.error('Use PNG, JPG, WEBP or SVG'); return; }
    setLogoUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await api.post('/upload', fd); setBranding(p => ({ ...p, logo_url: data.url })); toast.success('Logo uploaded'); }
    catch { toast.error('Logo upload failed'); }
    setLogoUploading(false);
    // Allow re-selecting the same file to trigger onChange again.
    if (e.target) e.target.value = '';
  };

  const handleSave = async () => {
    if (!meta.title.trim()) { toast.error('Catalog title is required'); return; }
    setSaving(true);
    try {
      // Validate: foreign currency requires an exchange rate when prices are visible.
      if (showPrices && currency.code !== 'INR') {
        const rate = parseFloat(currency.exchange_rate_to_inr);
        if (!rate || rate <= 0) {
          toast.error(`Enter the exchange rate (1 ${currency.code} = ? INR) before saving`);
          setSaving(false);
          return;
        }
      }
      const currencyPayload = {
        code: currency.code,
        symbol: currency.symbol,
        exchange_rate_to_inr: currency.code === 'INR' ? null : parseFloat(currency.exchange_rate_to_inr) || null,
        rounding_mode: currency.rounding_mode,
      };
      const payload = { ...meta, branding, language: catalogLang, back_cover: backCover, show_prices: showPrices, show_quantity: showQuantity, currency: currencyPayload, sections: sections.map(s => ({ id: s.id, name: s.name, product_ids: s.products.map(p => p.id), display_options: s.products.map(p => ({ display_mode: p._display_mode || 'table', selected_sku: p._display_mode === 'solo' ? (p._selected_sku || '') : '' })) })) };
      if (isEdit) { await api.put(`/catalogs/${id}`, payload); toast.success('Catalog updated'); }
      else { await api.post('/catalogs', payload); toast.success('Catalog created'); }
      navigate('/catalogs');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save catalog'); }
    setSaving(false);
  };

  const openDuplicateDialog = () => {
    setDuplicateForm({
      title: meta.title ? `${meta.title} (Copy)` : '',
      subtitle: meta.subtitle || '',
      client_name: backCover.name || '',
    });
    setDuplicateOpen(true);
  };

  const handleDuplicate = async () => {
    const newTitle = duplicateForm.title.trim();
    if (!newTitle) { toast.error('New catalog title is required'); return; }
    setDuplicating(true);
    try {
      const { data } = await api.post(`/catalogs/${id}/duplicate`, {
        title: newTitle,
        subtitle: duplicateForm.subtitle || null,
        client_name: duplicateForm.client_name || null,
      });
      toast.success('Catalog duplicated');
      setDuplicateOpen(false);
      navigate(`/catalogs/${data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate catalog');
    }
    setDuplicating(false);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Template name required'); return; }
    try { await api.post('/catalog-templates', { name: templateName.trim(), description: templateDesc, section_names: sections.map(s => s.name), cover_image: meta.cover_image, intro_note: meta.intro_note, footer_contact: meta.footer_contact, branding }); toast.success('Template saved'); setTemplateDialogOpen(false); setTemplateName(''); setTemplateDesc(''); fetchTemplates(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to save template'); }
  };
  const loadTemplate = (template) => {
    setMeta(prev => ({ ...prev, cover_image: template.cover_image || prev.cover_image, intro_note: template.intro_note || prev.intro_note, footer_contact: template.footer_contact || prev.footer_contact }));
    if (template.branding) setBranding(prev => ({ ...prev, ...template.branding }));
    if (template.section_names?.length > 0) { const ns = template.section_names.map((name, i) => ({ id: `section-${Date.now()}-${i}`, name, products: [] })); setSections(ns); setActiveSection(ns[0]?.id || null); }
    setLoadTemplateOpen(false); toast.success(`Template "${template.name}" loaded`);
  };

  const handleAddByCodes = async () => {
    if (!activeSection) { toast.error('Select or create a section first'); return; }
    const raw = codesInput.trim();
    if (!raw) { toast.error('Enter one or more product codes'); return; }
    setCodesLoading(true);
    try {
      const { data } = await api.post('/products/resolve-codes', { codes: raw });
      const { found = [], missing = [], requested = 0 } = data;
      if (found.length) {
        setSections(prev => prev.map(s => {
          if (s.id !== activeSection) return s;
          const existing = new Set(s.products.map(p => p.id));
          const toAdd = found.filter(p => !existing.has(p.id));
          return { ...s, products: [...s.products, ...toAdd] };
        }));
      }
      const alreadyCount = found.length - (sections.find(s => s.id === activeSection)?.products.filter(p => found.some(f => f.id === p.id)).length || 0);
      if (found.length && missing.length) {
        toast.success(`Added ${found.length} of ${requested} products`);
        toast.warning(`${missing.length} code${missing.length > 1 ? 's' : ''} not found: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`);
      } else if (found.length) {
        toast.success(`Added ${found.length} product${found.length > 1 ? 's' : ''}`);
      } else {
        toast.error(`No products found for: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`);
      }
      if (found.length) setCodesInput('');
      void alreadyCount;
    } catch { toast.error('Failed to resolve product codes'); }
    setCodesLoading(false);
  };

  const handleAiRecommend = async () => {
    if (!activeSection) { toast.error('Select a section first'); return; }
    const section = sections.find(s => s.id === activeSection); if (!section) return;
    setAiLoading(true); setAiReasoning('');
    try {
      const existingIds = sections.flatMap(s => s.products.map(p => p.id));
      const { data } = await api.post('/catalogs/recommend-products', { title: meta.title, subtitle: meta.subtitle, section_name: section.name, existing_product_ids: existingIds, brand: filters.brand, limit: 8 });
      if (data.recommendations?.length > 0) { setSections(prev => prev.map(s => { if (s.id !== activeSection) return s; const np = data.recommendations.filter(p => !s.products.some(ep => ep.id === p.id)); return { ...s, products: [...s.products, ...np] }; })); setAiReasoning(data.reasoning); toast.success(`Added ${data.recommendations.length} AI-recommended products`); }
      else toast.info('No recommendations available');
    } catch { toast.error('AI recommendation failed'); }
    setAiLoading(false);
  };

  const downloadPdf = async () => { if (!isEdit || !id) return; await downloadPdfWithCache(`/catalogs/${id}/pdf`, meta.title); };
  const getPreviewCatalog = () => ({
    ...meta,
    branding,
    language: catalogLang,
    back_cover: backCover,
    show_prices: showPrices,
    show_quantity: showQuantity,
    currency: {
      code: currency.code,
      symbol: currency.symbol,
      exchange_rate_to_inr: currency.code === 'INR' ? null : parseFloat(currency.exchange_rate_to_inr) || null,
      rounding_mode: currency.rounding_mode,
    },
    sections,
  });
  const clearFilters = () => setFilters({ search: '', brand: '', category: '', subcategory: '', deity: '', region: '', occasion: '', status: '', mrp_min: '', mrp_max: '' });
  const updateFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v === 'all' ? '' : v }));

  // iter29 — missing-quantities panel. Fetched whenever showQuantity flips ON
  // OR when the product selection changes (adding/removing products triggers
  // a recomputation so the panel never shows stale rows). Only queries after
  // the catalog is persisted (requires catalog_id).
  const refreshMissingQuantities = useCallback(async () => {
    if (!isEdit || !id || !showQuantity) { setMissingList([]); setPresentList([]); setMissingDrafts({}); return; }
    setQtyPanelLoading(true);
    try {
      const { data } = await api.get(`/catalogs/${id}/missing-quantities`);
      setMissingList(data.missing || []);
      setPresentList(data.present || []);
      // Seed drafts with empty strings so number inputs are controlled.
      const seed = {};
      (data.missing || []).forEach(m => { seed[m.product_code] = ''; });
      setMissingDrafts(seed);
    } catch (err) { devError('Missing quantities fetch failed', err); }
    setQtyPanelLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `sections` is intentional: refetch when product selection changes
  }, [isEdit, id, showQuantity, sections]);

  useEffect(() => { refreshMissingQuantities(); }, [refreshMissingQuantities]);

  const handleSaveMissingQuantities = async () => {
    // Validate every draft locally first — server will re-validate.
    const entries = [];
    const errors = [];
    for (const row of missingList) {
      const raw = missingDrafts[row.product_code];
      const s = (raw ?? '').trim();
      if (s === '') { errors.push(`${row.product_code}: enter a quantity (0 is valid)`); continue; }
      if (!/^\d+$/.test(s)) { errors.push(`${row.product_code}: whole non-negative number only`); continue; }
      entries.push({ product_code: row.product_code, quantity: parseInt(s, 10) });
    }
    if (entries.length === 0) { toast.error(errors[0] || 'Enter at least one quantity'); return; }
    if (errors.length) { toast.error(errors[0]); return; }
    setSavingQty(true);
    try {
      await api.post('/products/quantities', { entries });
      toast.success(`Saved ${entries.length} quantit${entries.length === 1 ? 'y' : 'ies'}. These update every catalog that has Show Quantity ON.`);
      await refreshMissingQuantities();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.errors?.[0]?.error || 'Save failed');
      toast.error(msg);
    }
    setSavingQty(false);
  };

  return (
    <div data-testid="catalog-builder-page" className="animate-fade-in">
      <button onClick={() => navigate('/catalogs')} className="flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#C5A059] mb-6" data-testid="back-to-catalogs"><ArrowLeft size={16} /><span>Back to Catalogs</span></button>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight">{isEdit ? 'Edit Catalog' : 'New Catalog'}</h1>
        <div className="flex gap-2 flex-wrap">
          {isEdit && <Button variant="outline" onClick={() => setHistoryOpen(true)} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="history-btn"><History size={16} className="mr-2" />History</Button>}
          {isEdit && catalogShareToken && <Button variant="outline" onClick={() => window.open(`/flipbook/${catalogShareToken}`, '_blank')} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="open-flipbook-btn"><BookOpen size={16} className="mr-2" />Flipbook</Button>}
          <Button variant="outline" onClick={() => setLoadTemplateOpen(true)} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="load-template-btn"><BookmarkCheck size={16} className="mr-2" />Load Template</Button>
          <Button variant="outline" onClick={() => setTemplateDialogOpen(true)} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="save-template-btn"><BookmarkPlus size={16} className="mr-2" />Save as Template</Button>
          <Button variant="outline" onClick={() => setPreviewOpen(true)} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="preview-catalog-btn"><Eye size={16} className="mr-2" />Preview</Button>
          {isEdit && <Button variant="outline" onClick={openDuplicateDialog} className="rounded-sm border-[#C5A059] text-sm text-[#C5A059] hover:bg-[#F2E8D5]" data-testid="duplicate-catalog-btn"><Copy size={16} className="mr-2" />Save as New Catalog</Button>}
          <Button onClick={handleSave} disabled={saving || (showQuantity && (meta.status === 'final' || meta.status === 'Final') && missingList.length > 0)} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="save-catalog-btn" title={showQuantity && (meta.status === 'final' || meta.status === 'Final') && missingList.length > 0 ? `${missingList.length} visible SKU(s) have no quantity — fill them in before finalising` : undefined}><Save size={16} className="mr-2" />{saving ? 'Saving...' : 'Save Catalog'}</Button>
        </div>
      </div>

      {/* Catalog Details */}
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg text-[#1A1A1A]">Catalog Details</h2>
          <Button variant="outline" size="sm" onClick={() => setShowBranding(!showBranding)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid="toggle-branding-btn"><Palette size={14} className="mr-1" />{showBranding ? 'Hide' : 'Show'} Branding</Button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><FieldLabel>Title *</FieldLabel><Input data-testid="catalog-title-input" value={meta.title} onChange={e => setMeta(p => ({ ...p, title: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
          <div><FieldLabel>Subtitle</FieldLabel><Input value={meta.subtitle} onChange={e => setMeta(p => ({ ...p, subtitle: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <FieldLabel>Cover Image</FieldLabel>
            <div className="flex items-center gap-3">
              {meta.cover_image ? (
                <div className="relative group">
                  <img src={getImageUrl(meta.cover_image)} alt="Cover" className="w-20 h-20 object-cover rounded-sm border border-[#E5E0D8]" />
                  <button type="button" onClick={() => setMeta(p => ({ ...p, cover_image: '' }))} className="absolute -top-2 -right-2 w-5 h-5 bg-[#9E2A2B] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={12} /></button>
                </div>
              ) : coverUploading ? <Loader2 size={20} className="text-[#C5A059] animate-spin" /> : (
                <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#E5E0D8] rounded-sm text-sm cursor-pointer hover:bg-[#F5F2EB] transition-colors" data-testid="cover-upload-btn">
                  <Upload size={16} className="text-[#C5A059]" /><span className="text-[#5C5C5C]">Upload Cover</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                </label>
              )}
            </div>
          </div>
          <div><FieldLabel>Status</FieldLabel>
            <Select value={meta.status} onValueChange={v => setMeta(p => ({ ...p, status: v }))}><SelectTrigger className="rounded-sm border-[#E5E0D8]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="final">Final</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent></Select>
          </div>
          <div><FieldLabel>Catalog Language</FieldLabel>
            <Select value={catalogLang} onValueChange={setCatalogLang}><SelectTrigger className="rounded-sm border-[#E5E0D8]" data-testid="catalog-language-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent></Select>
            {catalogLang !== 'en' && <p className="text-[9px] text-[#C5A059] mt-1">Bilingual: English + {LANGUAGES.find(l => l.code === catalogLang)?.label}</p>}
          </div>
        </div>

        {/* ============== PRICING SECTION ============== */}
        <div className="mt-2 pt-5 border-t border-dashed border-[#E5E0D8]" data-testid="pricing-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-serif text-base text-[#1A1A1A]">Pricing</h3>
              <p className="text-[11px] text-[#5C5C5C] mt-0.5">
                {showPrices
                  ? 'Prices are visible on the catalog. Hide prices for a price-on-request brochure.'
                  : 'Prices are hidden for this catalog.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${showPrices ? 'text-[#1A1A1A]' : 'text-[#888]'}`}>{showPrices ? 'Show Prices' : 'Hide Prices'}</span>
              <Switch
                checked={showPrices}
                onCheckedChange={setShowPrices}
                data-testid="show-prices-toggle"
                className="data-[state=checked]:bg-[#C5A059]"
              />
            </div>
          </div>
          <div className={`grid grid-cols-2 gap-4 ${!showPrices ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <FieldLabel>Catalog Currency</FieldLabel>
              <Select
                value={currency.code}
                onValueChange={(v) => {
                  const def = CURRENCIES.find(c => c.code === v);
                  setCurrency(prev => ({
                    ...prev,
                    code: v,
                    symbol: def?.symbol || prev.symbol,
                    // Reset rate when switching to INR
                    exchange_rate_to_inr: v === 'INR' ? '' : prev.exchange_rate_to_inr,
                  }));
                }}
                disabled={!showPrices}
              >
                <SelectTrigger className="rounded-sm border-[#E5E0D8]" data-testid="catalog-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel>Exchange Rate</FieldLabel>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder={currency.code === 'INR' ? 'Not required for INR' : `1 ${currency.code} = ? INR`}
                value={currency.exchange_rate_to_inr}
                onChange={(e) => setCurrency(prev => ({ ...prev, exchange_rate_to_inr: e.target.value }))}
                disabled={!showPrices || currency.code === 'INR'}
                className="rounded-sm border-[#E5E0D8]"
                data-testid="catalog-exchange-rate-input"
              />
              {showPrices && currency.code !== 'INR' && (
                <p className="text-[9px] text-[#888] mt-1">
                  e.g. ₹10,000 / {currency.exchange_rate_to_inr || 'rate'} ={' '}
                  {(() => {
                    const r = parseFloat(currency.exchange_rate_to_inr);
                    if (!r || r <= 0) return '—';
                    const v = Math.floor(10000 / r + 0.5);
                    return `${currency.symbol}${v.toLocaleString()}`;
                  })()}
                </p>
              )}
              {showPrices && (
                <p className="text-[9px] text-[#888] mt-1 italic">All prices are rounded to the nearest integer.</p>
              )}
            </div>
          </div>
          {!showPrices && (
            <p className="text-[11px] text-[#C5A059] mt-3 italic" data-testid="prices-hidden-notice">Prices are hidden for this catalog. Currency and exchange-rate fields are disabled.</p>
          )}
        </div>
        {/* ============== /PRICING SECTION ============== */}
        {/* ============== iter29 — SHOW QUANTITY SECTION ============== */}
        <div className="mt-2 pt-5 border-t border-dashed border-[#E5E0D8]" data-testid="quantity-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-serif text-base text-[#1A1A1A]">Live Quantity</h3>
              <p className="text-[11px] text-[#5C5C5C] mt-0.5">
                {showQuantity
                  ? 'Each product card shows the latest master quantity. Updates via Bulk Upload > Add Quantities flow through this catalog with its existing share link.'
                  : 'Quantities are hidden. Toggle on to show a compact Qty badge on every product card (independent of Show Prices).'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${showQuantity ? 'text-[#1A1A1A]' : 'text-[#888]'}`}>{showQuantity ? 'Show Quantity' : 'Hide Quantity'}</span>
              <Switch
                checked={showQuantity}
                onCheckedChange={setShowQuantity}
                data-testid="show-quantity-toggle"
                className="data-[state=checked]:bg-[#C5A059]"
              />
            </div>
          </div>
          {showQuantity && isEdit && (
            <div className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-sm p-4 mt-3" data-testid="missing-quantities-panel">
              {qtyPanelLoading ? (
                <p className="text-xs text-[#5C5C5C] italic flex items-center gap-2"><Loader2 size={12} className="animate-spin" />Checking quantities…</p>
              ) : missingList.length === 0 && presentList.length === 0 ? (
                <p className="text-xs text-[#5C5C5C] italic">Add products to this catalog to see which ones need quantities.</p>
              ) : missingList.length === 0 ? (
                <p className="text-xs text-[#2D6A4F]" data-testid="all-quantities-set"><Check size={12} className="inline mr-1" />All {presentList.length} selected product{presentList.length === 1 ? '' : 's'} have a saved quantity. Latest Excel/manual updates flow through automatically.</p>
              ) : (
                <>
                  <p className="text-xs font-semibold text-[#9E2A2B] mb-2" data-testid="missing-quantities-count">
                    {missingList.length} selected product{missingList.length === 1 ? '' : 's'} {missingList.length === 1 ? 'has' : 'have'} no saved quantity yet. Enter values below — a saved <strong>0</strong> is valid and renders as &ldquo;Qty 0&rdquo;.
                  </p>
                  <div className="max-h-64 overflow-y-auto border border-[#E5E0D8] rounded-sm bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F5F2EB] sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Code</th>
                          <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Name</th>
                          <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Size</th>
                          <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missingList.map(row => (
                          <tr key={row.product_code} className="border-t border-[#E5E0D8]" data-testid={`missing-qty-row-${row.product_code}`}>
                            <td className="py-1.5 px-3 font-mono text-[11px] text-[#C5A059]">{row.product_code}</td>
                            <td className="py-1.5 px-3 text-[#1A1A1A]">{row.product_name}</td>
                            <td className="py-1.5 px-3 text-[#5C5C5C]">{row.size || '—'}</td>
                            <td className="py-1.5 px-3">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={missingDrafts[row.product_code] ?? ''}
                                onChange={e => setMissingDrafts(prev => ({ ...prev, [row.product_code]: e.target.value }))}
                                className="w-24 h-7 text-xs rounded-sm border-[#E5E0D8]"
                                data-testid={`missing-qty-input-${row.product_code}`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-[10px] text-[#888] italic">Manual entries update the master value used by every quantity-enabled catalog. A later Excel upload can replace them, and vice-versa.</p>
                    <Button size="sm" onClick={handleSaveMissingQuantities} disabled={savingQty} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm text-xs" data-testid="save-quantities-btn">
                      {savingQty ? <><Loader2 size={12} className="mr-1 animate-spin" />Saving…</> : <><Save size={12} className="mr-1" />Save Quantities</>}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          {showQuantity && !isEdit && (
            <p className="text-[11px] text-[#C5A059] italic">Save this catalog first to see any missing quantities for the selected products.</p>
          )}
        </div>
        {/* ============== /SHOW QUANTITY SECTION ============== */}
        <div className="mb-4"><FieldLabel>Intro Note</FieldLabel><Textarea value={meta.intro_note} onChange={e => setMeta(p => ({ ...p, intro_note: e.target.value }))} className="rounded-sm border-[#E5E0D8]" rows={2} /></div>

        {showBranding && (
          <div className="mt-6 pt-6 border-t border-[#E5E0D8]" data-testid="branding-section">
            <h3 className="font-serif text-base text-[#1A1A1A] mb-4">Distributor Branding</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><FieldLabel>Company Name</FieldLabel><Input data-testid="branding-company" value={branding.company_name} onChange={e => setBranding(p => ({ ...p, company_name: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
              <div><FieldLabel>Tagline</FieldLabel><Input value={branding.tagline} onChange={e => setBranding(p => ({ ...p, tagline: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>Distributor Logo</FieldLabel>
                <div className="flex items-center gap-3">
                  {branding.logo_url ? (
                    <div className="relative group" data-testid="branding-logo-preview">
                      {/* Uploaded PNG/JPG/SVG preview — max 96×48 with contain so aspect
                          ratio never squishes. Same padding + border treatment as
                          Cover Image thumb above. */}
                      <img src={getImageUrl(branding.logo_url)} alt="Logo" className="h-12 max-w-[8rem] object-contain rounded-sm border border-[#E5E0D8] bg-white p-1" />
                      <button type="button" onClick={() => setBranding(p => ({ ...p, logo_url: '' }))} className="absolute -top-2 -right-2 w-5 h-5 bg-[#9E2A2B] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid="branding-logo-remove-btn"><X size={12} /></button>
                      <label className="ml-3 inline-flex items-center gap-1 text-xs text-[#C5A059] hover:underline cursor-pointer" data-testid="branding-logo-replace-btn">
                        <Upload size={12} /> Replace
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} />
                      </label>
                    </div>
                  ) : logoUploading ? <Loader2 size={20} className="text-[#C5A059] animate-spin" /> : (
                    <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#E5E0D8] rounded-sm text-sm cursor-pointer hover:bg-[#F5F2EB] transition-colors" data-testid="branding-logo-upload-btn">
                      <Upload size={16} className="text-[#C5A059]" /><span className="text-[#5C5C5C]">Upload Logo</span>
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} />
                    </label>
                  )}
                </div>
                <p className="text-[10px] text-[#888] mt-1">Recommended: transparent PNG, 500×250px or higher. Max 5&nbsp;MB.</p>
              </div>
              <div><FieldLabel>Primary Color</FieldLabel><div className="flex gap-2 items-center"><input type="color" value={branding.primary_color} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} className="w-10 h-9 rounded-sm border border-[#E5E0D8] cursor-pointer" /><Input value={branding.primary_color} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} className="rounded-sm border-[#E5E0D8] flex-1" /></div></div>
            </div>
          </div>
        )}
      </div>

      {/* Back Cover */}
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 mb-6" data-testid="back-cover-section">
        <h2 className="font-serif text-lg text-[#1A1A1A] mb-4">Back Cover Contact Details</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><FieldLabel>Contact Name</FieldLabel><Input data-testid="back-cover-name" value={backCover.name} onChange={e => setBackCover(p => ({ ...p, name: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
          <div><FieldLabel>Phone</FieldLabel><Input data-testid="back-cover-phone" value={backCover.phone} onChange={e => setBackCover(p => ({ ...p, phone: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><FieldLabel>Email</FieldLabel><Input value={backCover.email} onChange={e => setBackCover(p => ({ ...p, email: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
          <div><FieldLabel>Website</FieldLabel><Input value={backCover.website} onChange={e => setBackCover(p => ({ ...p, website: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
        </div>
        <div><FieldLabel>Address</FieldLabel><Textarea value={backCover.address} onChange={e => setBackCover(p => ({ ...p, address: e.target.value }))} className="rounded-sm border-[#E5E0D8]" rows={2} /></div>
      </div>

      {/* Builder */}
      <div className="grid grid-cols-5 gap-6">
        {/* Product Browser with Advanced Filters */}
        <div className="col-span-2 bg-white border border-[#E5E0D8] rounded-sm p-4" data-testid="product-browser">
          {/* Add by Code */}
          <div className="mb-4 pb-4 border-b border-[#E5E0D8]" data-testid="add-by-code-panel">
            <div className="flex items-center gap-2 mb-2">
              <Hash size={14} className="text-[#C5A059]" />
              <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A]">Add Products by Code</span>
            </div>
            <div className="flex gap-2 items-start">
              <div className="relative flex-1">
                <Textarea
                  value={codesInput}
                  onChange={e => setCodesInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleAddByCodes(); } }}
                  placeholder="e.g. AR-AEEB-008, AR-ARDM-001, BULK-183929"
                  rows={2}
                  className="rounded-sm border-[#E5E0D8] text-xs resize-none pr-7"
                  data-testid="codes-input"
                />
                {codesInput && (
                  <button type="button" onClick={() => setCodesInput('')} className="absolute top-1.5 right-1.5 text-[#9E2A2B] hover:bg-red-50 rounded p-0.5" data-testid="clear-codes-btn" title="Clear"><X size={12} /></button>
                )}
              </div>
              <Button onClick={handleAddByCodes} disabled={codesLoading || !codesInput.trim()} size="sm" className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm h-auto py-2 shrink-0" data-testid="add-by-codes-btn">
                {codesLoading ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" />Add</>}
              </Button>
            </div>
            <p className="text-[9px] text-[#888] mt-1.5">Paste up to 500 codes separated by comma, newline, semicolon, or pipe. Press Ctrl/⌘+Enter to add.</p>
          </div>

          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-base text-[#1A1A1A]">Browse Products</h3>
            <span className="text-[10px] text-[#5C5C5C]">{browseProducts.length} found</span>
          </div>
          <div className="flex gap-2 mb-2">
            <div className="relative flex-1"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C5C5C]" /><Input value={filters.search} onChange={e => updateFilter('search', e.target.value)} placeholder="Search..." className="pl-8 rounded-sm border-[#E5E0D8] text-sm h-9" data-testid="browse-search" /></div>
            <Button variant="outline" size="sm" onClick={() => setShowAdvFilters(!showAdvFilters)} className={`rounded-sm border-[#E5E0D8] h-9 ${showAdvFilters ? 'bg-[#F2E8D5]' : ''}`} data-testid="toggle-adv-filters"><Filter size={14} /></Button>
            {Object.values(filters).some(Boolean) && <Button variant="outline" size="sm" onClick={clearFilters} className="rounded-sm border-[#E5E0D8] h-9" title="Clear filters"><RotateCcw size={14} /></Button>}
          </div>

          {showAdvFilters && (
            <div className="grid grid-cols-2 gap-2 mb-3 p-3 bg-[#F5F2EB] rounded-sm" data-testid="advanced-filters">
              <Select value={filters.brand || 'all'} onValueChange={v => updateFilter('brand', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white"><SelectValue placeholder="Brand" /></SelectTrigger><SelectContent><SelectItem value="all">All Brands</SelectItem>{taxonomy.brand.map(b => <SelectItem key={b.id} value={b.value}>{b.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.category || 'all'} onValueChange={v => updateFilter('category', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent><SelectItem value="all">All Categories</SelectItem>{taxonomy.category.map(c => <SelectItem key={c.id} value={c.value}>{c.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.subcategory || 'all'} onValueChange={v => updateFilter('subcategory', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white"><SelectValue placeholder="Subcategory" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{taxonomy.subcategory.map(s => <SelectItem key={s.id} value={s.value}>{s.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.deity || 'all'} onValueChange={v => updateFilter('deity', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white"><SelectValue placeholder="Deity" /></SelectTrigger><SelectContent><SelectItem value="all">All Deities</SelectItem>{taxonomy.deity.map(d => <SelectItem key={d.id} value={d.value}>{d.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.region || 'all'} onValueChange={v => updateFilter('region', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white"><SelectValue placeholder="Region" /></SelectTrigger><SelectContent><SelectItem value="all">All Regions</SelectItem>{taxonomy.region.map(r => <SelectItem key={r.id} value={r.value}>{r.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.occasion || 'all'} onValueChange={v => updateFilter('occasion', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white"><SelectValue placeholder="Occasion" /></SelectTrigger><SelectContent><SelectItem value="all">All Occasions</SelectItem>{taxonomy.occasion.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.status || 'all'} onValueChange={v => updateFilter('status', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="discontinued">Discontinued</SelectItem></SelectContent></Select>
              <div className="flex gap-1"><Input type="number" placeholder="Min MRP" value={filters.mrp_min} onChange={e => updateFilter('mrp_min', e.target.value)} className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" /><Input type="number" placeholder="Max MRP" value={filters.mrp_max} onChange={e => updateFilter('mrp_max', e.target.value)} className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" /></div>
            </div>
          )}

          <div className="max-h-[500px] overflow-y-auto space-y-1">
            {browseProducts.map(p => (
              <div key={p.id} className="flex items-center gap-2 p-2 hover:bg-[#F5F2EB] rounded-sm cursor-pointer transition-colors" onClick={() => addProductToSection(p)} data-testid={`browse-product-${p.id}`}>
                {p.cover_image ? <img src={getImageUrl(p.cover_image)} alt="" className="w-8 h-8 object-cover rounded-sm" /> : <div className="w-8 h-8 img-placeholder rounded-sm text-[8px]">N/A</div>}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#1A1A1A] truncate">{p.product_name}</p>
                  <p className="text-[10px] text-[#5C5C5C]">{p.product_code} | {p.brand} | {'\u20B9'}{p.mrp}</p>
                </div>
                <Plus size={14} className="text-[#C5A059] shrink-0" />
              </div>
            ))}
            {browseProducts.length === 0 && <p className="text-sm text-[#5C5C5C] text-center py-8">No products found</p>}
          </div>
        </div>

        {/* Sections */}
        <div className="col-span-3" data-testid="sections-panel">
          <div className="flex gap-2 mb-4">
            <Input value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="New section name..." className="rounded-sm border-[#E5E0D8] text-sm" data-testid="new-section-input" onKeyDown={e => e.key === 'Enter' && addSection()} />
            <Button onClick={addSection} variant="outline" className="rounded-sm border-[#E5E0D8] shrink-0" data-testid="add-section-btn"><FolderPlus size={16} className="mr-2" />Add Section</Button>
            <Button onClick={handleAiRecommend} disabled={aiLoading || !activeSection} variant="outline" className="rounded-sm border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5] shrink-0" data-testid="ai-recommend-btn">
              {aiLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}{aiLoading ? 'Thinking...' : 'AI Suggest'}
            </Button>
          </div>
          {aiReasoning && <div className="mb-4 p-3 bg-[#F2E8D5] border border-[#C5A059]/20 rounded-sm" data-testid="ai-reasoning"><div className="flex items-start gap-2"><Sparkles size={14} className="text-[#C5A059] mt-0.5 shrink-0" /><p className="text-xs text-[#5C5C5C] leading-relaxed">{aiReasoning}</p></div></div>}

          {sections.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E5E0D8] rounded-sm p-12 text-center"><FolderPlus size={32} className="mx-auto text-[#E5E0D8] mb-3" /><p className="text-sm text-[#5C5C5C]">Add sections to organize your catalog</p></div>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sections.map(s => `sec::${s.id}`)} strategy={verticalListSortingStrategy}>
                <div className="space-y-4">
                  {sections.map(section => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      isActive={activeSection === section.id}
                      onActivate={() => setActiveSection(section.id)}
                      onRemove={() => removeSection(section.id)}
                      onRenameStart={() => startRenameSection(section)}
                    >
                      {{
                        header: renamingSectionId === section.id ? (
                          <div className="flex items-center gap-2 flex-1 min-w-0" onClick={e => e.stopPropagation()}>
                            <Input
                              autoFocus
                              value={renamingValue}
                              onChange={e => setRenamingValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); commitRenameSection(); }
                                if (e.key === 'Escape') { e.preventDefault(); cancelRenameSection(); }
                              }}
                              maxLength={80}
                              className="h-7 text-sm rounded-sm border-[#C5A059] focus:ring-[#C5A059]"
                              data-testid={`section-rename-input-${section.id}`}
                            />
                            <button onClick={commitRenameSection} className="text-[#C5A059] hover:bg-[#F2E8D5] p-1 rounded" title="Save (Enter)" data-testid={`section-rename-save-${section.id}`}><Check size={14} /></button>
                            <button onClick={cancelRenameSection} className="text-[#5C5C5C] hover:bg-[#F5F2EB] p-1 rounded" title="Cancel (Esc)" data-testid={`section-rename-cancel-${section.id}`}><X size={14} /></button>
                          </div>
                        ) : (
                          <h4 className="font-medium text-sm text-[#1A1A1A] truncate" data-testid={`section-title-${section.id}`}>{section.name}</h4>
                        ),
                        body: (
                          <>
                            <SortableContext items={section.products.map(p => p.id)} strategy={verticalListSortingStrategy}>{section.products.map(product => <SortableProduct key={product.id} product={product} onRemove={(pid) => removeProductFromSection(section.id, pid)} onUpdateDisplay={(pid, patch) => updateProductDisplay(section.id, pid, patch)} />)}</SortableContext>
                            {section.products.length === 0 && <p className="text-xs text-[#5C5C5C] text-center py-4 border border-dashed border-[#E5E0D8] rounded-sm">Click products from the browser to add</p>}
                          </>
                        ),
                      }}
                    </SortableSection>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <CatalogPreviewModal catalog={getPreviewCatalog()} open={previewOpen} onClose={() => setPreviewOpen(false)} onDownloadPdf={isEdit ? downloadPdf : null} />
      {isEdit && <CatalogHistoryPanel catalogId={id} open={historyOpen} onClose={() => setHistoryOpen(false)} onRollback={() => fetchCatalog()} />}

      <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]" data-testid="duplicate-catalog-dialog">
          <DialogHeader><DialogTitle className="font-serif text-lg">Save as New Catalog</DialogTitle></DialogHeader>
          <p className="text-xs text-[#5C5C5C] -mt-2">Clone every section, product, pricing rule and branding setting into a brand-new catalog. The original remains untouched.</p>
          <div className="space-y-4 py-4">
            <div><FieldLabel>New Title *</FieldLabel><Input data-testid="duplicate-title-input" value={duplicateForm.title} onChange={e => setDuplicateForm(p => ({ ...p, title: e.target.value }))} className="rounded-sm border-[#E5E0D8]" autoFocus /></div>
            <div><FieldLabel>Subtitle</FieldLabel><Input data-testid="duplicate-subtitle-input" value={duplicateForm.subtitle} onChange={e => setDuplicateForm(p => ({ ...p, subtitle: e.target.value }))} className="rounded-sm border-[#E5E0D8]" /></div>
            <div><FieldLabel>Distributor / Client Name</FieldLabel><Input data-testid="duplicate-client-input" value={duplicateForm.client_name} onChange={e => setDuplicateForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Optional — appears on the back cover" className="rounded-sm border-[#E5E0D8]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateOpen(false)} disabled={duplicating} className="rounded-sm border-[#E5E0D8]" data-testid="duplicate-cancel-btn">Cancel</Button>
            <Button onClick={handleDuplicate} disabled={duplicating} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="confirm-duplicate-btn">
              {duplicating ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Copy size={14} className="mr-2" />}
              {duplicating ? 'Duplicating…' : 'Create Copy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}><DialogContent className="bg-white rounded-sm border-[#E5E0D8]"><DialogHeader><DialogTitle className="font-serif text-lg">Save as Template</DialogTitle></DialogHeader><div className="space-y-4 py-4"><div><FieldLabel>Template Name</FieldLabel><Input data-testid="template-name-input" value={templateName} onChange={e => setTemplateName(e.target.value)} className="rounded-sm border-[#E5E0D8]" autoFocus /></div><div><FieldLabel>Description</FieldLabel><Input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)} className="rounded-sm border-[#E5E0D8]" /></div></div><DialogFooter><Button variant="outline" onClick={() => setTemplateDialogOpen(false)} className="rounded-sm border-[#E5E0D8]">Cancel</Button><Button onClick={handleSaveTemplate} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="confirm-save-template">Save</Button></DialogFooter></DialogContent></Dialog>
      <Dialog open={loadTemplateOpen} onOpenChange={setLoadTemplateOpen}><DialogContent className="bg-white rounded-sm border-[#E5E0D8]"><DialogHeader><DialogTitle className="font-serif text-lg">Load Template</DialogTitle></DialogHeader><div className="py-4 max-h-[400px] overflow-y-auto">{templates.length === 0 ? <p className="text-sm text-[#5C5C5C] text-center py-8">No templates yet</p> : <div className="space-y-2">{templates.map(t => (<div key={t.id} className="flex items-center justify-between p-4 border border-[#E5E0D8] rounded-sm hover:bg-[#F5F2EB] cursor-pointer" onClick={() => loadTemplate(t)} data-testid={`template-item-${t.id}`}><div><p className="text-sm font-medium text-[#1A1A1A]">{t.name}</p>{t.description && <p className="text-xs text-[#5C5C5C]">{t.description}</p>}<p className="text-[10px] text-[#C5A059] mt-1">{t.section_names?.length || 0} sections</p></div><Button size="sm" className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm text-xs">Use</Button></div>))}</div>}</div></DialogContent></Dialog>
    </div>
  );
}
